import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './SegmentedControl.module.css';

export interface SegmentOption<V extends string | number> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<V extends string | number> {
  value: V;
  onChange: (value: V) => void;
  options: readonly SegmentOption<V>[];
  label: ReactNode;
  /** Hides the visible label (it stays available to screen readers). */
  hideLabel?: boolean;
}

/** Single-choice group with radio semantics and arrow-key navigation (roving tabindex). */
export function SegmentedControl<V extends string | number>({
  value,
  onChange,
  options,
  label,
  hideLabel = false,
}: SegmentedControlProps<V>) {
  const id = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (from: number, step: 1 | -1) => {
    const n = options.length;
    for (let i = 1; i <= n; i++) {
      const idx = (from + step * i + n * i) % n;
      const opt = options[idx];
      if (opt && !opt.disabled) {
        onChange(opt.value);
        refs.current[idx]?.focus();
        return;
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(index, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(index, -1);
    }
  };

  const selectedIndex = options.findIndex((o) => o.value === value);

  return (
    <div className={styles['field']}>
      <span id={`${id}-label`} className={hideLabel ? 'visually-hidden' : styles['label']}>
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className={styles['group']}>
        {options.map((opt, i) => {
          const checked = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              disabled={opt.disabled}
              tabIndex={checked || (selectedIndex === -1 && i === 0) ? 0 : -1}
              className={styles['segment']}
              onClick={() => {
                onChange(opt.value);
              }}
              onKeyDown={(e) => {
                onKeyDown(e, i);
              }}
            >
              {checked ? (
                <span className={styles['tick']} aria-hidden="true">
                  {'✓'}
                </span>
              ) : null}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
